import React from 'react';
import useDaikinControl from '../../hooks/useDaikinControl';
import { formatLondon } from '../../utils/dateFormatting';
import ErrorBanner from './ErrorBanner';

const fmt = (v, suffix = '°C') => (v == null ? '-' : `${v}${suffix}`);

// "Fan 3/3", "Fan auto", "Fan quiet", or "-" for units that don't report one.
// Heating should show the top step: the unit samples return air in its own
// warm plume, and a low fan makes it throttle before the room has warmed.
export const fanLabel = (u) => {
    if (!u.fan_mode) return '-';
    if (u.fan_mode !== 'fixed') return `Fan ${u.fan_mode}`;
    if (u.fan_speed == null) return 'Fan fixed';
    return u.fan_speed_max != null ? `Fan ${u.fan_speed}/${u.fan_speed_max}` : `Fan ${u.fan_speed}`;
};
const fanBelowMax = (u) =>
    u.operation_mode === 'heating' && u.fan_speed_max != null &&
    (u.fan_mode !== 'fixed' || u.fan_speed < u.fan_speed_max);

// Daikin status and manual override. Reads are served from the backend's cached
// snapshot, so they cost nothing against the 200/day Onecta budget unless the
// cache is stale. `canControl` hides the controls for viewers; the backend
// enforces it regardless.
const DaikinControlCard = ({ canControl = true }) => {
    const {
        status, loading, error, conflict, busy, pending,
        refresh: fetchStatus, switchPower: handlePower, applySetpoint: handleApplySetpoint, nudgeSetpoint,
        applyFanSpeed,
    } = useDaikinControl();

    const fetchedLabel = formatLondon(status?.fetched_at, 'full');
    const units = status?.units || [];
    const obj = status?.objective;
    const heating = obj && obj.power === 'on';
    const boosting = heating && obj.phase === 'boost' && obj.target != null;
    const objectiveLabel = !heating
        ? 'Automation idle: no active heating window'
        : boosting
            ? `Automation: boosting to reach ${obj.target}°C (units set to ${fmt(obj.setpoint)})`
            : `Automation: holding ${fmt(obj.target != null ? obj.target : obj.setpoint)}`;
    const roomLabel = heating && obj.measured != null
        ? `Room sensor ${fmt(obj.measured)}${obj.measured_at ? ` at ${formatLondon(obj.measured_at, 'time')}` : ''}`
        : null;
    const conflictUnitName =
        conflict && (units.find((u) => u.device_id === conflict.device_id)?.name || conflict.device_id);

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Daikin Units</h3>
                <div className="flex items-center gap-2">
                    {status && status.device_count != null && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            {status.device_count} {status.device_count === 1 ? 'unit' : 'units'}
                        </span>
                    )}
                    <button
                        onClick={fetchStatus}
                        disabled={loading}
                        className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40"
                        title="Fetch the latest state (uses a daily API call only if the last read is over a minute old)"
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Automation objective — these units are driven by the comfort
                schedule; manual controls below are an override. */}
            {status && (
                <div className="mb-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between">
                        <span>
                            {objectiveLabel}
                            {roomLabel && <span className="text-gray-400"> · {roomLabel}</span>}
                        </span>
                        {canControl && <span className="text-gray-300">controls below override the automation</span>}
                    </div>
                    {/* Why the units show a setpoint above the comfort target. */}
                    {heating && (
                        <p className="mt-1.5 text-[11px] text-gray-400 leading-snug">
                            {boosting
                                ? `The units are set above the ${obj.target}°C target on purpose. `
                                : 'While the room is below its target the units are set above it on purpose. '}
                            Each unit measures the air at its own intake, high on the wall and inside its warm
                            outflow, so at the target setpoint it throttles back while the room below is still
                            cold. The automation keeps the setpoint above the target until the building's room
                            sensor reads the target, then drops it to the target and the units hold it themselves.
                        </p>
                    )}
                </div>
            )}

            <ErrorBanner message={error} className="mb-3" />

            {/* Stale-write guard tripped: the unit changed in Onecta, so we did NOT
                send the command. The display now shows the real current value. */}
            {conflict && (
                <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <strong>{conflictUnitName}</strong> was changed in Onecta (now{' '}
                    {conflict.field === 'setpoint' ? `${conflict.actual}°C`
                        : conflict.field === 'fan_speed' ? `fan ${conflict.actual}` : conflict.actual}). Your change wasn't
                    applied. Review the current state and try again if you still want it.
                </div>
            )}

            {!status && loading && <div className="text-sm text-gray-400">Loading...</div>}
            {status && units.length === 0 && !loading && (
                <div className="text-sm text-gray-400">No Daikin units found.</div>
            )}

            <div className="divide-y divide-gray-100">
                {units.map((u) => {
                    const isOn = u.power === 'on';
                    const isBusy = !!busy[u.device_id];
                    const pend = pending[u.device_id];
                    const dirty = pend != null && parseFloat(pend) !== u.setpoint;
                    return (
                        <div key={u.device_id} className="py-3 first:pt-0">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-700">
                                        {u.name || u.model || u.device_id}
                                    </span>
                                    <span
                                        className={`text-[11px] px-2 py-0.5 rounded-full ${
                                            u.online ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
                                        }`}
                                    >
                                        {u.online ? 'online' : 'offline'}
                                    </span>
                                    {u.auto_state === 'accepted' && <Badge tone="green">automated</Badge>}
                                    {u.auto_state === 'overridden' && <Badge tone="amber">overridden</Badge>}
                                    {u.schedule_enabled && <Badge tone="indigo">scheduled</Badge>}
                                    {u.holiday_mode && <Badge tone="sky">holiday</Badge>}
                                    {u.mode_conflict && <Badge tone="amber">mode conflict</Badge>}
                                    {u.error_state && <Badge tone="red">error {u.error_code || ''}</Badge>}
                                </div>
                                <div className="text-xs text-gray-400">
                                    Room {fmt(u.room_temperature)} · Out {fmt(u.outdoor_temperature)} ·{' '}
                                    <span className="capitalize">{u.operation_mode || '-'}</span> ·{' '}
                                    <span
                                        className={fanBelowMax(u) ? 'text-amber-500' : ''}
                                        title={fanBelowMax(u)
                                            ? 'Below full speed: the unit will throttle before the room is warm. The automation sets full speed when it next switches heating on.'
                                            : 'Fan speed in the current mode'}
                                    >
                                        {fanLabel(u)}
                                    </span>
                                </div>
                            </div>

                            {canControl && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {/* Power */}
                                <div className="flex items-center gap-0 shadow-sm">
                                    <button
                                        onClick={() => handlePower(u, 'on')}
                                        disabled={isBusy || isOn}
                                        className={`px-3 py-1.5 rounded-l-lg text-sm font-medium border transition-colors ${
                                            isOn
                                                ? 'bg-green-500 text-white border-green-500'
                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                        } disabled:opacity-60`}
                                    >
                                        On
                                    </button>
                                    <button
                                        onClick={() => handlePower(u, 'off')}
                                        disabled={isBusy || !isOn}
                                        className={`px-3 py-1.5 rounded-r-lg text-sm font-medium border border-l-0 transition-colors ${
                                            !isOn
                                                ? 'bg-gray-700 text-white border-gray-700'
                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                        } disabled:opacity-60`}
                                    >
                                        Off
                                    </button>
                                </div>

                                {/* Fan: one button per fixed step. Heating wants the top step
                                    (the unit samples return air in its own plume); anything
                                    lower is shown amber in the status line above. */}
                                {u.fan_speed_max != null && (
                                    <div className="flex items-center gap-0 shadow-sm" title="Fan speed">
                                        {Array.from({ length: u.fan_speed_max }, (_, i) => i + 1).map((step) => {
                                            const active = u.fan_mode === 'fixed' && u.fan_speed === step;
                                            const first = step === 1;
                                            const last = step === u.fan_speed_max;
                                            return (
                                                <button
                                                    key={step}
                                                    onClick={() => applyFanSpeed(u, step)}
                                                    disabled={isBusy || active}
                                                    aria-label={`Fan speed ${step}`}
                                                    className={`px-3 py-1.5 text-sm font-medium border transition-colors ${
                                                        first ? 'rounded-l-lg' : 'border-l-0'
                                                    } ${last ? 'rounded-r-lg' : ''} ${
                                                        active
                                                            ? 'bg-gray-700 text-white border-gray-700'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                                    } disabled:opacity-60`}
                                                >
                                                    {first ? `Fan ${step}` : step}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Setpoint stepper (clamped to device bounds) */}
                                <div className="flex items-center gap-0 shadow-sm ml-auto">
                                    <button
                                        onClick={() => nudgeSetpoint(u, -0.5)}
                                        aria-label="Lower setpoint"
                                        disabled={isBusy || pend == null || (u.setpoint_min != null && pend <= u.setpoint_min)}
                                        className="px-3 py-1.5 border border-gray-200 rounded-l-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                                    >
                                        −
                                    </button>
                                    <span className="px-3 py-1.5 border-t border-b border-gray-200 text-sm text-gray-700 min-w-[58px] text-center">
                                        {pend == null ? '-' : `${pend}°C`}
                                    </span>
                                    <button
                                        onClick={() => nudgeSetpoint(u, 0.5)}
                                        aria-label="Raise setpoint"
                                        disabled={isBusy || pend == null || (u.setpoint_max != null && pend >= u.setpoint_max)}
                                        className="px-3 py-1.5 border border-gray-200 rounded-r-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                                    >
                                        +
                                    </button>
                                </div>
                                <button
                                    onClick={() => handleApplySetpoint(u)}
                                    disabled={isBusy || !dirty}
                                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40"
                                >
                                    Set
                                </button>
                            </div>
                            )}

                            {/* Be honest that a manual nudge is temporary when a schedule runs. */}
                            {u.schedule_enabled && (
                                <div className="text-[11px] text-gray-400 mt-1">
                                    On an Onecta schedule
                                    {u.schedule_next_action ? ` (next: ${u.schedule_next_action})` : ''}
                                    : manual changes hold until the next scheduled action.
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {status && (
                <div className="flex items-center justify-between text-[11px] text-gray-400 pt-2 mt-2 border-t border-gray-100">
                    <span>
                        {status.calls_remaining != null ? `${status.calls_remaining} API calls left today` : ''}
                        {status.from_cache && (
                            <span className={status.stale ? 'text-amber-500' : ''}>
                                {' · '}
                                {status.stale ? 'cached (budget saved)' : 'cached'}
                            </span>
                        )}
                    </span>
                    <span>updated {fetchedLabel}</span>
                </div>
            )}
        </div>
    );
};

const TONES = {
    green: 'bg-green-50 text-green-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    sky: 'bg-sky-50 text-sky-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
};
const Badge = ({ tone, children }) => (
    <span className={`text-[11px] px-2 py-0.5 rounded-full ${TONES[tone] || 'bg-gray-100 text-gray-500'}`}>
        {children}
    </span>
);

export default DaikinControlCard;
